import { useBoolean } from 'ahooks';
import { Dictionary, uniqBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { RoleType, Team } from 'api/models';
import { Button } from 'components/atoms/button';
import { PlaceholderCreateOrEditModal } from 'components/organisms/placeholders/create-edit-modal';
import { useTranslation } from 'hooks/use-translation';
import {
  createProjectPlaceholderMutationFn,
  useDefaultNativeTeamsInAccount,
} from 'queries/placeholder';
import {
  useVendorRolesQuery,
  useRolesWithPlaceholderQuery,
} from 'queries/roles';
import { ProjectsWithMembers } from '../types';
import {
  AddToProjectModalFooter as Footer,
  ModalTitle,
  ModalWithStyledFooter,
} from './common';
import { AddTeamsModalContent } from './modal-content/team-content';

interface Props {
  teamMembership: Record<string, Team[]>;
  teamsAvailableForProject: Team[];
  projectLookup: Dictionary<ProjectsWithMembers>;
  projectId?: number;
  onInvited: (projectId: number, users: Team[]) => void;
  onTeamsAddHandler?: (
    projectId: number,
    teams: Team[],
    _newTeam?: Team,
  ) => Promise<void>;
  createTeamHandler?: ReturnType<typeof createProjectPlaceholderMutationFn>;
  onCancel: () => void;
}

export const AddTeamsModal: FC<Props> = ({
  teamMembership,
  projectId,
  projectLookup,
  onInvited,
  onTeamsAddHandler,
  createTeamHandler,
  onCancel,
  teamsAvailableForProject,
}) => {
  const { data: roles = [] } = useVendorRolesQuery();
  const { data: rolesWithPlaceholders = [] } = useRolesWithPlaceholderQuery();
  const vendorRoles = useMemo(
    () =>
      rolesWithPlaceholders.filter(({ type }) => type !== RoleType.Customer),
    [rolesWithPlaceholders],
  );
  const { data: defaultTeams = [] } = useDefaultNativeTeamsInAccount();
  const projectName =
    projectId != null ? projectLookup[projectId]?.projectName : undefined;
  const assignedTeamIds = useMemo(
    () =>
      projectId ? teamMembership[projectId].map(({ teamId }) => teamId) : [],
    [projectId, teamMembership],
  );
  const teams = useMemo(
    () => uniqBy([...defaultTeams, ...teamsAvailableForProject], 'teamId'),
    [defaultTeams, teamsAvailableForProject],
  );

  const teamsNotInProject = useMemo(
    () =>
      projectId
        ? teams.filter(({ teamId }) => !assignedTeamIds.includes(teamId))
        : [],
    [assignedTeamIds, teams, projectId],
  );
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [
    newTeamModalVisibility,
    { setTrue: showNewTeamModal, setFalse: hideNewTeamModal },
  ] = useBoolean(false);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);

  const modalPrimaryTitle = useTranslation(
    'resource_management.add_placeholders.title',
  );
  const createNewTeamCta = useTranslation(
    'resource_management.add_placeholders.new_placeholder.primary_cta',
  );

  const closeAddTeamsModal = useCallback(() => {
    setSelectedTeams([]);
    onCancel();
  }, [onCancel]);

  const onAddTeams = useCallback(
    (_teams: Team[], newTeam?: Team) => {
      if (projectId != null) {
        if (onTeamsAddHandler != null) {
          setIsSubmitted(true);
          onTeamsAddHandler(projectId, _teams, newTeam)
            .then(() => {
              setSelectedTeams([]);
              onInvited(projectId, _teams);
            })
            .finally(() => {
              setIsSubmitted(false);
            });
        } else {
          setSelectedTeams([]);
          onInvited(projectId, _teams);
        }
      }
    },
    [onInvited, onTeamsAddHandler, projectId],
  );

  const handleNewTeamCreation = useCallback(
    (team: Team) => {
      if (projectId) {
        hideNewTeamModal();
        setSelectedTeams([...selectedTeams, team]);
        onAddTeams([...selectedTeams, team], team);
      }
    },
    [hideNewTeamModal, onAddTeams, projectId, selectedTeams],
  );

  return (
    <>
      <ModalWithStyledFooter
        visible={projectId != null && !newTeamModalVisibility}
        destroyOnClose
        onCancel={closeAddTeamsModal}
        title={
          <ModalTitle
            primaryTitle={modalPrimaryTitle}
            secondaryTitle={projectName}
          />
        }
        footer={
          <Footer>
            <Button
              type="text"
              disabled={isSubmitted}
              onClick={closeAddTeamsModal}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              type="primary"
              loading={isSubmitted}
              disabled={selectedTeams.length === 0}
              onClick={() => {
                onAddTeams(selectedTeams);
              }}
            >
              <FormattedMessage
                id="resource_management.add_placeholders.add"
                values={{ count: selectedTeams.length }}
              />
            </Button>
          </Footer>
        }
      >
        {projectId != null ? (
          <AddTeamsModalContent
            teams={teamsNotInProject}
            roles={roles}
            selectedTeams={selectedTeams}
            updateSelectedTeams={setSelectedTeams}
            newPlaceholderClickHandler={showNewTeamModal}
          />
        ) : null}
      </ModalWithStyledFooter>
      {newTeamModalVisibility ? (
        <PlaceholderCreateOrEditModal
          roles={vendorRoles}
          placeholders={teams}
          mutationFn={createTeamHandler}
          primaryButtonLabel={createNewTeamCta}
          save={handleNewTeamCreation}
          cancel={hideNewTeamModal}
        />
      ) : null}
    </>
  );
};
